import type {
  StaffOperationsControlReason,
  StaffOperationsIntentState,
  StaffOperationsTaskId,
} from "@lafayette/schemas";
import type { IconName } from "../ui/icon";

// The words of the Operations page. A state is never shown by colour alone: every badge has a glyph and a word.

export const taskLabels: Readonly<Record<StaffOperationsTaskId, string>> = {
  "catalog-import.validate": "Catalog import: validate",
  "catalog-import.process": "Catalog import: process",
  "catalog-item.enrich": "Catalog item: enrich",
  "catalog-item.embed": "Catalog item: embed",
  "customer-memory.extract": "Customer memory: extract",
  "messaging.membership-sync": "Messaging: member sync",
  "messaging.system-notice": "Messaging: system notice",
  "messaging.staff-access-revoke": "Messaging: staff access revoke",
  "messaging.staff-message-remove": "Messaging: staff message remove",
  "messaging.human-only-handoff": "Messaging: ask a person (AI away)",
  "request.sla-check": "Request: SLA check",
  "payment-request.expire": "Payment request: expire",
  "payment-request.reconcile": "Payment request: reconcile",
  "notification.deliver": "Notification: deliver",
  "notification.identity-revoke": "Notification: staff push identity revoke",
  "notification.receipt-check": "Notification: push receipt check",
  "trip.reminder": "Trip: reminder",
  "itinerary-item.weather-check": "Itinerary item: weather check",
  "customer-privacy.export": "Privacy: customer export",
  "customer-privacy.delete": "Privacy: customer deletion",
};

export const stateLabels: Readonly<Record<StaffOperationsIntentState, string>> = {
  PENDING: "Waiting",
  CLAIMED: "Running",
  SUCCEEDED: "Done",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

/** The icon beside the word of a state (decoration, `aria-hidden`: the word is the state). Each state has its own icon. */
export const stateGlyphs: Readonly<Record<StaffOperationsIntentState, IconName>> = {
  PENDING: "hourglass",
  CLAIMED: "play",
  SUCCEEDED: "checkmark-circle-02",
  FAILED: "cancel-circle",
  CANCELLED: "unavailable",
};

export const classLabels = {
  critical: "Critical",
  interactive: "Interactive",
  batch: "Batch",
} as const;

export const decisionLabels = {
  ACTED: "Acted",
  SUPERSEDED: "Superseded (the subject changed)",
  SUPPRESSED_RESOLVED: "Suppressed (already resolved)",
  SUPPRESSED_PREFERENCE: "Suppressed (customer preference)",
  SUPPRESSED_DUPLICATE: "Suppressed (duplicate)",
  SKIPPED_PROVIDER_UNAVAILABLE: "Skipped (provider unavailable)",
  SKIPPED_PAUSED: "Skipped (paused)",
  RETRY_EXHAUSTED: "Retries used up",
  NEEDS_RECONCILIATION: "Needs reconciliation",
} as const;

export const errorClassLabels = {
  RETRYABLE: "Retryable",
  RATE_LIMITED: "Rate limited",
  AMBIGUOUS: "Ambiguous",
  TERMINAL: "Terminal",
} as const;

export const runnerLabels = {
  worker: "Trigger.dev run",
  api: "API lane",
  "in-process": "In-process",
} as const;

export const controlReasonLabels: Readonly<Record<StaffOperationsControlReason, string>> = {
  INCIDENT: "Incident",
  PROVIDER_OUTAGE: "Provider outage",
  COST: "Cost",
  MAINTENANCE: "Maintenance",
  PROOF: "Proof run",
};

export const cancelReasonLabels = {
  SUBJECT_CANCELLED: "Subject cancelled",
  SUBJECT_DELETED: "Subject deleted",
  SUPERSEDED: "Superseded",
  OPERATOR: "Operator",
} as const;
